import React, { useState, useRef, useContext, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import ModalHeading from '../../components/shared/modal-heading';
import { AccountsContext } from '../../components/context/account-context';
import { prependDefault } from '../../utils/structure-options-utils';
import { uploadMedia } from '../../services/upload-service';
import { updateMeter } from '../../services/meters-service';
import AttachmentInput from '../../components/shared/attachment-input';
import Loader from '../../components/shared/loader';

const EditModal = ({ selectedRow, setSelectedRow, globalDataloading, setIsEditModalOpen, setData, types, fetchMeters, isVehicleTab = false, vehicles }) => {
    const { accountId } = useContext(AccountsContext);
    const [imagePreview, setImagePreview] = useState(selectedRow?.media?.signedThumbUrl || null);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        let timer;
        if (uploadStatus.message && uploadStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUploadStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [uploadStatus]);

    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            const reader = new FileReader();
            reader.onloadend = async () => {
                setImagePreview(reader.result);

                try {
                    const response = await uploadMedia(accountId, file, 'meter', selectedRow.meterId);
                    if (response.success) {
                        const updatedRow = {
                            ...selectedRow,
                            media: response.data.data
                        };
                        setSelectedRow(updatedRow);
                        setData(prevData => prevData.map(row =>
                            row.meterId === selectedRow.meterId ? updatedRow : row
                        ));
                        setUploadStatus({ message: 'Receipt uploaded successfully!', type: 'success' });
                    } else {
                        setImagePreview(null);
                        setUploadStatus({
                            message: response.data.message || 'Failed to upload receipt',
                            type: 'error'
                        });
                    }
                } finally {
                    setIsUploading(false);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        if (isUploading) return;
        const updatedRow = {
            ...selectedRow,
            media: null
        };
        setImagePreview(null);
        setSelectedRow(updatedRow);
        setData(prevData => prevData.map(row =>
            row.meterId === selectedRow.meterId ? updatedRow : row
        ));
        setUploadStatus({ message: '', type: '' });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFieldUpdate = async (value, fieldName) => {

        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            time: 'meterTime',
            vehicle: 'vehicleId',
            value: 'value',
            type: 'type',
            status: 'status',
            note: 'note'
        };

        const payload = {};
        if (fieldName === 'time') {
            payload[fieldMap[fieldName]] = Math.floor(new Date(value).getTime() / 1000).toString();
        } else if (fieldName === 'vehicle') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'type') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'value') {
            payload[fieldMap[fieldName]] = parseFloat(value);
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        try {
            const response = await updateMeter(accountId, selectedRow.meterId, payload);
            if (response.success) {
                const updatedRow = { ...selectedRow };
                switch (fieldName) {
                    case 'time':
                        updatedRow.meterTime = payload.meterTime;
                        break;
                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicleOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'type':
                        updatedRow.type = value;
                        break;
                    case 'value':
                        updatedRow.value = parseFloat(value);
                        break;
                    default:
                        updatedRow[fieldName] = value;
                }
                setSelectedRow(updatedRow);
                setData(prevData => prevData.map(row =>
                    row.meterId === selectedRow.meterId ? updatedRow : row
                ));
                setUpdateStatus({ message: `${labelMap[fieldName] || fieldName} updated successfully!`, type: 'success' });
            } else {
                setUpdateStatus({
                    message: response.data.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
                // throw new Error('API update failed');
            }
        } catch (error) {
            setUpdateStatus({
                message: error.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
            throw error;
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const labelMap = {
        time: 'Time',
        vehicle: 'Vehicle',
        value: 'Value',
        type: 'Type',
        status: 'Status',
        note: 'Note'
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const vehicleOptions = prependDefault(vehicles.map(v => ({ value: v.vehicleId, label: v.label })), 'Vehicle');
    const typeOptions = prependDefault(types.map(v => ({ value: v.slug, label: v.label })), 'Type');

    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Meter Detail Information</ModalHeading>

            <button
                className='hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full'
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className='text-xl' />
            </button>

            {(uploadStatus.message || updateStatus.message) && (
                <div className={`px-4 py-2 text-sm ${(uploadStatus.type || updateStatus.type) === 'success' ? 'text-green-600 bg-green-100' :
                    (uploadStatus.type || updateStatus.type) === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'
                    } rounded-md mx-4`}>
                    {uploadStatus.message || updateStatus.message}
                </div>
            )}

            {
                globalDataloading ?
                    <Loader />
                    :
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <EditableField
                            label="Time"
                            name="time"
                            value={(selectedRow?.meterTime) || ''}
                            onChange={handleFieldUpdate}
                            type="datetime-local"
                            isLoading={loadingFields.time}
                            isEditable={false}
                        />

                        <EditableField
                            label="Vehicle"
                            name="vehicle"
                            type="select"
                            value={selectedRow?.vehicle?.vehicleId || ''}
                            options={vehicleOptions}
                            onChange={handleFieldUpdate}
                            isEditable={false}
                            isLoading={loadingFields.vehicle}
                        />

                        <EditableField
                            label="Value"
                            name="value"
                            value={selectedRow?.value || ''}
                            onChange={handleFieldUpdate}
                            type="number"
                            isLoading={loadingFields.value}
                        />

                        <EditableField
                            label="Type"
                            name="type"
                            type="select"
                            value={selectedRow?.type || ''}
                            options={typeOptions}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.type}
                        />

                        <EditableField
                            label="Status"
                            name="status"
                            value={selectedRow?.status || 'active'}
                            onChange={handleFieldUpdate}
                            type="select"
                            isEditable={false}
                            isLoading={loadingFields.status}
                        />

                        <EditableField
                            label="Note"
                            name="note"
                            type='textarea'
                            rows={2}
                            value={selectedRow?.note || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.note}
                        />

                        <AttachmentInput
                            fileInputRef={fileInputRef}
                            handleImageUpload={handleImageUpload}
                            triggerFileInput={triggerFileInput}
                            imagePreview={imagePreview}
                            selectedRow={selectedRow}
                            handleRemoveImage={handleRemoveImage}
                            isUploading={isUploading}
                            accountId={accountId}
                            attachLabel='Attach Meter Media'
                            uploadLabel={'Upload'}
                        />
                    </div>
            }
        </div>
    );
};

export default EditModal;